import * as Yup from "yup";

export const getAgentSignupSchema = (isRequired) =>
  Yup.object().shape({
    company: Yup.object().shape({
      name: Yup.string()
        .trim()
        .required("Company name is required")
        .min(1, "Name must be at least 1 character")
        .max(100, "Name must not exceed 100 characters"),
      phoneNumber: Yup.string().required("Phone number is required"),
      licenseNumber: Yup.string().trim().required("Licence number is required"),
      websiteUrl: Yup.string()
        .required("Website URL is required")
        .max(500, "URL must not exceed 500 characters")
        .test("is-valid-url", "Must be a valid URL", (value) => {
          if (!value) return false; // Required field
          const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*$/;
          return urlPattern.test(value);
        }),
      addressLine1: Yup.string()
        .trim()
        .required("Address Line 1 is required")
        .max(200, "Address Line 1 should not exceed 200 characters"),
      addressLine2: Yup.string()
        .trim()
        .nullable()
        .max(200, "Address Line 2 should not exceed 200 characters"),
      city: Yup.string().trim().required("City is required"),
      stateAndProvince: Yup.string()
        .trim()
        .required("State/Province is required"),
      countryId: Yup.string().required("Country is required"),
    }),
    officeHours: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Office hour name is required"),
          isActive: Yup.boolean().required("Active status is required"),
          startTime: Yup.string().required("Start time is required"),
          endTime: Yup.string().required("End time is required"),
          startTimeError: Yup.string()
            .nullable()
            .oneOf([null], "Start time error must be null"),
          endTimeError: Yup.string()
            .nullable()
            .oneOf([null], "End time error must be null"),
        })
      )
      .min(7, "Valid office hour is required")
      .test(
        "at-least-one-active",
        "At least one day must be active",
        (value) => value?.some((item) => item.isActive) // Custom test
      ),
    agent: Yup.object().shape({
      firstName: isRequired
        ? Yup.string()
            .trim()
            .required("First name is required")
            .max(100, "First name must not exceed 100 characters")
        : Yup.string(),
      lastName: isRequired
        ? Yup.string()
            .trim()
            .required("Last name is required")
            .max(100, "Last name must not exceed 100 characters")
        : Yup.string(),
      phoneNumber: Yup.string().required("Phone number is required"),
      email: isRequired
        ? Yup.string()
            .required("Email is required")
            .max(100, "Email must not exceed 100 characters")
            .test(
              "is-valid-email",
              "Invalid email address",
              (value) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            )
        : Yup.string(),
      languageSpokenIds: Yup.array()
        .min(1, "At least one language is required")
        .required("Languages spoken is required"),
      preferredContactMethodId: Yup.string().required(
        "Preferred contact method is required"
      ),
      password: isRequired
        ? Yup.string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(
              /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?`~])[a-zA-Z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]+$/,
              "Password must include letters, numbers, and at least one special character"
            )
        : Yup.string(),
      confirmPassword: isRequired
        ? Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm password is required")
        : Yup.string(),
    }),
    emergencyContactInfo: Yup.object().shape({
      files: Yup.array().nullable(),
      headshotfiles: Yup.array().nullable(),
    }),
    document: Yup.array()
      .min(1, "At least one certificate is required")
      .required("Registration certificate is required"),
  });
